import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { refusalText } from "helpers/apiRefusal"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"
import { runCompetitionOperation } from "../../competitionOperation"
// entryAdmissionText imports entryMessage too; each module calls the other only inside functions, so the cycle is safe.
import { admissionRefusal } from "./entryAdmissionText"

const INVALID_REQUEST = "The request was not valid, so nothing was changed. Reload the competition before trying again."

const entryMessages = {
  ENTRY_POINTS_ALREADY_SAVED: "Entries cannot be added after competition points have been saved, including a partial allocation.",
  ENTRY_MAIN_DRAW_GENERATED: "Wild cards cannot be added after the Main Draw has been generated.",
  ENTRY_ADMISSION_UNVERIFIED: "Entry availability could not be verified. Retry before adding a participant.",
  ENTRY_CAPACITY_UNVERIFIED: "Current entry capacity could not be verified. Reload the competition.",
  PARTICIPANT_NOT_ACTIVE: "The selected participant does not have an active account.",
  INCOMPLETE_PARTICIPANT: "Complete the participant's birth date and gender in their profile before registering.",
  PARTICIPANT_SUSPENDED: "The selected participant is suspended and cannot register.",
  EVENT_ENTRY_LIMIT_REACHED: "A selected participant has reached the entry limit for this event.",
  // A failed Accounts activity read: not terminal in Events, so the quote can pass on retry.
  REGISTRATION_ACCOUNT_UNVERIFIED: "A participant's account could not be read to check this competition's restrictions. Retry the quote.",
  ENTRY_FEE_UNVERIFIED: "The entry fee could not be verified. Check the competition fees and retry.",
  // A Withdraw or Move preview read that failed (Events answers its dependencies' failures with ENTRY_CONTEXT_UNAVAILABLE).
  ENTRY_CONTEXT_UNAVAILABLE: "The entry, draw and payment could not be checked right now. Nothing was changed. Retry the preview.",
  ENTRY_PREVIEW_UNAVAILABLE: "The entry, draw and payment could not be checked right now. Nothing was changed. Retry the preview.",
  ENTRY_MOVE_CHECK_UNAVAILABLE: "The entry could not be checked right now. Nothing was moved. Retry the move.",
  // The page's lists are out of date (staleEntryCodes): only reloading them helps.
  ENTRY_NOT_FOUND: "This entry is no longer active in this competition. It was withdrawn or removed meanwhile. Reload the entries to see the current lists.",
  EVENT_NOT_FOUND: "This event is no longer available.",
  COMPETITION_NOT_FOUND: "This competition is no longer available.",
  ENTRY_NOT_CONFIRMED: "This entry is no longer confirmed. Reload the entries to see its current status.",
  ENTRY_OPERATION_NOT_RECEIVED: "The request was not saved and can no longer run. Review a fresh preview before trying again.",
  ENTRY_PREVIEW_CHANGED: "The competition or payment details changed. Review a fresh preview before confirming again.",
  ENTRY_REASON_REQUIRED: "Enter a reason for this action.",
  ERROR_PERMISSIONS: "Your account no longer has permission for this action.",
  OPERATION_PAYLOAD_CONFLICT: "This operation already belongs to another request. Recover its result before continuing.",
  COMPETITION_REACTIVATION_REQUIRED: "Reactivate this competition before adding or promoting entries.",
  ALREADY_REGISTERED_TO_COMPETITION: "One of these participants already has an entry or a registration in progress.",
  MAIN_LIST_FULL: "No eligible Main List place is available.",
  NO_MORE_WILDCARDS_AVAILABLE: "No reserved WildCard place is available.",
  // Refusals Payments reports for an administrative operation (API ADR 0014).
  CLOSED_BY_ADMIN: "Closed by an administrator.",
  ENTRY_OPERATION_CLOSED: "The operation was already closed.",
  INSUFFICIENT_BALANCE: "The payer's wallet no longer covers the fee.",
  REGISTRATION_IN_PROGRESS: "Another registration for this participant is in progress.",
  REGISTRATION_PARTICIPANT_CONFLICT: "A participant already has another entry in this competition.",
  WALLET_CURRENCY_CONFLICT: "The payer's wallet currency changed, so the wallet could not be charged or credited.",
  ADMINISTRATIVE_ENTRY_REFUSED: "Payments refused this operation for an unrecognized reason, so the entry was not changed. Report it with the Operation ID from Action Logs.",
  WALLET_EFFECT_CONFLICT: "The payer's wallet already records a different charge for this operation. Check the wallet before acting.",
  // Refusals Events reports when an add or a withdrawal runs.
  COMPETITION_FEES_NOT_SET: "This competition has no fees yet, so the entry was not added. Set the competition's fees first.",
  COMPETITION_FEES_NOT_AVAILABLE: "This competition has no fee in the payer's wallet currency, so the entry was not added. Add that fee to the competition first.",
  USER_INVALID_FEE_TAX: "The competition fees give no entry fee for the payer, so the entry was not added. Check the competition fees.",
  PARTNER_USER_INVALID_FEE_TAX: "The competition fees give no entry fee for the partner, so the entry was not added. Check the competition fees.",
  EVENTS_ENTRY_FAILED: "The competition did not confirm the entry, so it was not added.",
  WITHDRAWAL_TARGET_CHANGED: "The entry changed before the withdrawal ran, so it was not withdrawn. Review the entry and withdraw it again if needed.",
  INVALID_PARTICIPANT: "The selected participants do not match this competition. Close Add entry and select them again.",
  INVALID_ENTRY_REASON: "The reason is too long. Use at most 2000 characters.",
  INVALID_ENTRY_ACTION: INVALID_REQUEST,
  INVALID_ENTRY_OPERATION: INVALID_REQUEST,
  INVALID_REFUND_CHOICE: INVALID_REQUEST,
  INVALID_ENTRY_TYPE: INVALID_REQUEST,
  // The competition's entry fence is held by a draw, points, event completion or another add or move: the change is refused at
  // once and never applied later (decision T9).
  ENTRY_MOVE_OPERATION_IN_PROGRESS: "Another operation is using this competition (a draw, points, event completion, or another entry being added or moved). Nothing was changed. Try again when it finishes.",
}

// The words for an entry refusal `code`, an admission refusal included (without names), or null when the code is not known.
export const knownEntryMessage = code => entryMessages[code] || admissionRefusal(code) || null

// An unknown code still reads as a sentence, and keeps the code for support; a result without a code was never confirmed.
export const entryMessage = code => knownEntryMessage(code)
  || (code ? `The operation could not be completed (${code}).` : "The result could not be confirmed.")

// A Withdraw or Move preview read the gateway refused at its session gate (401 SESSION_EXPIRED from isLoggedIn or isAdmin): a
// retry only helps after signing in again.
export const ENTRY_READ_SESSION_EXPIRED_TEXT = "Your session has expired, so the entry could not be checked. Nothing was changed. Sign in again, then retry."

// The words for a failed entry read: the session text for a 401, a 4xx code the entry texts know, otherwise (a masked 5xx, an
// unknown code, no answer) the text of `fallbackCode`.
export const entryReadMessage = (error, fallbackCode) => error?.response?.status === 401 ? ENTRY_READ_SESSION_EXPIRED_TEXT
  : refusalText(error, knownEntryMessage, entryMessage(fallbackCode))

// Refusals that mean the page's entry lists are out of date: the entry, its event or its competition is gone, or the entry is
// no longer confirmed. A retry meets the same answer, so the screen offers "Reload entries" instead.
export const staleEntryCodes = ["ENTRY_NOT_FOUND", "EVENT_NOT_FOUND", "COMPETITION_NOT_FOUND", "ENTRY_NOT_CONFIRMED"]

// A read that Events refused (a 4xx) with one of staleEntryCodes.
export const staleEntryRead = error => {
  const status = error?.response?.status
  return status >= 400 && status < 500 && staleEntryCodes.includes(error.response.data?.message)
}

export default function useEntryOperation({ eventId, competitionId, userId, onConfirmed }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const storageKey = `entry-operation:${userId}:${eventId}:${competitionId}`
  const active = useRef(null), confirmation = useRef(onConfirmed)
  const [state, setState] = useState({})
  confirmation.current = onConfirmed
  const observe = useCallback((operation, submit, firstSubmission = false) => {
    active.current?.runner?.cancel()
    const request = { operation }
    active.current = request
    setState({ status: "pending", operation })
    request.runner = runCompetitionOperation({ operation, socket, submit, firstSubmission, operationKind: "entry-managed", messageForCode: entryMessage,
      read: id => get(`/admin/entry-operations/${id}`),
      onProgress: progress => { if (active.current === request) setState({ ...progress, status: "pending", operation }) },
    })
    return request.runner.promise.then(result => {
      if (active.current !== request || result.status === "cancelled") return result
      // A progress record can carry a raw code (an accepted Add another admin closed); the final result shows only its own words.
      setState(previous => ({ ...previous, ...result, message: result.message, code: result.code, operation }))
      if (["saved", "error"].includes(result.status)) {
        try { sessionStorage.removeItem(storageKey) } catch { /* Only this actor's operation can be recovered. */ }
        active.current = null
        if (result.status === "saved") confirmation.current?.(result)
      }
      return result
    })
  }, [socket, storageKey])
  useEffect(() => {
    setState({})
    if (userId) {
      try {
        const operation = JSON.parse(sessionStorage.getItem(storageKey) || "null")
        if (operation?.eventId === eventId && operation.competitionId === competitionId && operation.operationId && operation.correlationId) observe(operation)
      } catch { /* Invalid storage is never a command. */ }
    }
    return () => { active.current?.runner?.cancel(); active.current = null }
  }, [eventId, competitionId, observe, storageKey, userId])
  const save = input => {
    if (active.current) return
    if (!userId) { setState({ status: "error", message: "Reload to identify your signed-in account before saving." }); return }
    const operation = { eventId, competitionId, action: input.action, ...(input.entryId && { entryId: input.entryId }), operationId: newPlayerOperationId(), correlationId: newPlayerOperationId() }
    try { sessionStorage.setItem(storageKey, JSON.stringify(operation)) } catch {
      setState({ status: "error", message: "Session storage is required to recover the operation after reload." }); return
    }
    return observe(operation, () => post(`/admin/entry-operations/${eventId}/${competitionId}`, { ...input, operationId: operation.operationId, socketClientId },
      { headers: { "X-Correlation-Id": operation.correlationId } }), true)
  }
  const recover = () => {
    const operation = active.current?.operation || state.operation
    if (operation) return observe(operation, () => post(`/admin/entry-operations/${operation.operationId}/retry`, {
      eventId: operation.eventId, competitionId: operation.competitionId, action: operation.action, socketClientId,
    }, { headers: { "X-Correlation-Id": operation.correlationId } }))
  }
  return { ...state, pending: ["pending", "unknown"].includes(state.status), save, recover }
}
